import { IUserDetails, IUserType } from '../../interfaces/user.interface'
import { IFacultyStudent } from '../Faculties/IFaculty'
import { ICourse } from 'interfaces/ICourse'
import useUserAuthStatus from './useUserAuthStatus'
import { ISchool } from 'interfaces/school.interface'
import { IPaymentsPlan } from 'interfaces/IPaymentsPlan'

export interface IAuthDetails {
  authToken?: string
  userType?: IUserType
  userDetails?: IUserDetails
  userFaculties: IFacultyStudent[]
}

const useUserAuthSchoolDetails = () => {
  const userAuthHook = useUserAuthStatus()
  const authDetails = userAuthHook.authDetails

  const getCourseIdsInUserFaculties = () => {
    const courseIds: string[] = []
    authDetails?.userFaculties?.forEach(f => {
      const _courses = f?.faculty_full_object?.courses || []
      courseIds.push(..._courses)
    })
    return courseIds
  }

  const getProgramIdsInUserFaculties = () => {
    const programIds: string[] = []
    authDetails?.userFaculties?.forEach(f => {
      const _programs = f?.faculty_full_object?.programs || []
      programIds.push(..._programs)
    })
    return programIds
  }

  const getAvailableCoursesForUser = async (opts: {
    courses: ICourse[]
    school: ISchool
  }): Promise<ICourse[]> => {
    if (!opts.courses) {
      return []
    }

    if (!opts.school?.limit_discovery_to_faculties) {
      return opts.courses
    }

    if (!authDetails) {
      return []
    }

    if (['admin'].includes(authDetails?.userType || '')) {
      return opts.courses
    }

    const userFacultiesCourseIds = getCourseIdsInUserFaculties()

    const userVisibleCourses = opts.courses?.filter(course =>
      userFacultiesCourseIds.includes(course._id),
    )

    return userVisibleCourses
  }

  const getAvailableProgramsForUser = async (opts: {
    programs: IPaymentsPlan[]
    school: ISchool
  }): Promise<IPaymentsPlan[]> => {
    if (!opts.programs) {
      return []
    }

    if (!opts.school?.limit_discovery_to_faculties) {
      return opts.programs
    }

    if (!authDetails) {
      return []
    }

    if (['admin'].includes(authDetails?.userType || '')) {
      return opts.programs
    }

    const userFacultiesProgramIds = getProgramIdsInUserFaculties()

    const userVisiblePrograms = opts.programs?.filter(program =>
      userFacultiesProgramIds.includes(program?._id),
    )

    return userVisiblePrograms
  }

  const checkIfUserCanViewCourseDetails = (opts: {
    school?: ISchool
    courseId: string
  }) => {
    if (!opts.school?.limit_discovery_to_faculties) {
      return true
    }
    if (!authDetails) {
      return false
    }

    if (['admin'].includes(authDetails?.userType || '')) {
      return true
    }

    const userFacultiesCourseIds = getCourseIdsInUserFaculties()
    return userFacultiesCourseIds.includes(opts.courseId)
  }

  const checkIfUserCanViewProgramDetails = (opts: {
    programId: string
    schoolDetails: ISchool
  }) => {
    if (!opts.schoolDetails?.limit_discovery_to_faculties) {
      return true
    }
    if (!authDetails) {
      return false
    }

    if (['admin'].includes(authDetails?.userType || '')) {
      return true
    }

    const userFacultiesProgramIds = getProgramIdsInUserFaculties()
    return userFacultiesProgramIds.includes(opts.programId)
  }

  return {
    getCourseIdsInUserFaculties,
    getProgramIdsInUserFaculties,
    getAvailableCoursesForUser,
    getAvailableProgramsForUser,
    checkIfUserCanViewCourseDetails,
    checkIfUserCanViewProgramDetails,
  }
}

export default useUserAuthSchoolDetails
